import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { ShieldCheck, Plus, Pencil, Trash2, Check, Search, X, Users, ChevronDown, ChevronRight, Info, Zap } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PERMS = {
  dashboard: { view: true },
  negocios:  { view: false, create: false, edit: false, delete: false, export: false, mover_cards: false, ganhar_perder: false, somente_proprios: false, pipelines_permitidos: null, mover_negocios: false, liberar_isento: false },
  empresas:  { view: false, create: false, edit: false, delete: false, export: false },
  contatos:  { view: false, create: false, edit: false, delete: false, export: false },
  produtos:  { view: false, create: false, edit: false, delete: false, export: false },
  tarefas:   { view: false, create: false, edit: false, delete: false },
  email:     { view: false, create: false, edit: false, delete: false, enviar: false },
  relatorios: { view: false },
  automacoes: { view: false, create: false, edit: false, delete: false },
  leads:      { view: false, create: false, edit: false, delete: false },
  contratos:  { view: false, emitir: false, delete: false, editar_signatario_ia: false },
  cobrancas:  { view: false, emitir: false, delete: false },
  whatsapp:   { view: false, enviar: false },
  telefonia:  { view: false, ligar: false },
  arquivos:   { view: false, upload: false, delete: false },
  config:    { campos: false, pipeline: false, perfis: false, usuarios: false, aparencia: false, auditoria: false, geral: false, modulos: false, formularios: false, tarefas_config: false, integracoes: false, importar: false, robin_round: false },
};

const MODULE_LABELS = { negocios: "Negócios", leads: "Leads", empresas: "Empresas", contatos: "Contatos", produtos: "Produtos", tarefas: "Tarefas", email: "E-mail", relatorios: "Relatórios", automacoes: "Automações", contratos: "Contratos", cobrancas: "Cobranças", whatsapp: "WhatsApp", telefonia: "Telefonia (GoTo)", arquivos: "Arquivos" };
const CRM_ACTIONS = [
  { key: "view", label: "Visualizar" }, { key: "create", label: "Criar" },
  { key: "edit", label: "Editar" }, { key: "delete", label: "Excluir" }, { key: "export", label: "Exportar" },
];
const BASIC_ACTIONS = [
  { key: "view", label: "Visualizar" }, { key: "create", label: "Criar" },
  { key: "edit", label: "Editar" }, { key: "delete", label: "Excluir" },
];
const EMAIL_ACTIONS = [
  { key: "view", label: "Visualizar" }, { key: "create", label: "Criar" },
  { key: "edit", label: "Editar" }, { key: "delete", label: "Excluir" }, { key: "enviar", label: "Enviar campanhas" },
];
const REPORT_ACTIONS = [
  { key: "view", label: "Visualizar" },
];
const COBRANCA_ACTIONS = [
  { key: "view", label: "Visualizar" }, { key: "emitir", label: "Emitir cobranças" }, { key: "delete", label: "Excluir" },
];
const CONTRATO_ACTIONS = [
  { key: "view", label: "Visualizar" }, { key: "emitir", label: "Emitir contratos" }, { key: "delete", label: "Excluir" }, { key: "editar_signatario_ia", label: "Editar signatário da IA" },
];
const WHATSAPP_ACTIONS = [
  { key: "view", label: "Ver conversas" }, { key: "enviar", label: "Enviar mensagens" },
];
const TELEFONIA_ACTIONS = [
  { key: "view", label: "Ver gravações" }, { key: "ligar", label: "Fazer ligações" },
];
const ARQUIVO_ACTIONS = [
  { key: "view", label: "Visualizar" }, { key: "upload", label: "Enviar arquivos" }, { key: "delete", label: "Excluir" },
];
const CONFIG_LABELS = { campos: "Campos", pipeline: "Pipeline", perfis: "Perfis", usuarios: "Usuários", aparencia: "Aparência", auditoria: "Auditoria", geral: "Geral", modulos: "Módulos", formularios: "Formulários", tarefas_config: "Tarefas", integracoes: "Integrações", robin_round: "Robin Round", importar: "Importar" };

export default function PerfisPage() {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState(JSON.parse(JSON.stringify(DEFAULT_PERMS)));
  const [expanded, setExpanded] = useState({ negocios: true, empresas: false, contatos: false, produtos: false, tarefas: false, email: false, relatorios: false, automacoes: false, config: false });
  const [allPipelines, setAllPipelines] = useState(true); // true = null (all pipelines)
  const [aiCooldown, setAiCooldown] = useState(0);
  const [error, setError] = useState("");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#06B6D4";
  const tc50 = appearance.colors?.["50"] || "#ECFEFF";

  const fetchProfiles = useCallback(async () => {
    try { const res = await axios.get(`${API}/profiles`, { withCredentials: true }); setProfiles(res.data); } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);
  const fetchPipelines = useCallback(async () => {
    try { const res = await axios.get(`${API}/pipelines`, { withCredentials: true }); setPipelines(res.data); } catch (err) { console.error(err); }
  }, []);
  useEffect(() => { fetchProfiles(); fetchPipelines(); }, [fetchProfiles, fetchPipelines]);

  const openNew = () => {
    setEditProfile(null); setName(""); setPermissions(JSON.parse(JSON.stringify(DEFAULT_PERMS)));
    setAllPipelines(true); setAiCooldown(0); setError(""); setExpanded({ negocios: true, empresas: false, contatos: false, produtos: false, tarefas: false, email: false, relatorios: false, automacoes: false, config: false });
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditProfile(p); setName(p.name);
    const perms = JSON.parse(JSON.stringify({ ...DEFAULT_PERMS, ...p.permissions }));
    setPermissions(perms);
    const pp = p.permissions?.negocios?.pipelines_permitidos;
    setAllPipelines(pp === null || pp === undefined);
    setAiCooldown(p.ai_cooldown_minutes || 0);
    setError(""); setExpanded({ negocios: true, empresas: false, contatos: false, produtos: false, tarefas: false, email: false, relatorios: false, automacoes: false, config: false });
    setDialogOpen(true);
  };

  const setPerm = (module, key, value) => {
    setPermissions(prev => ({ ...prev, [module]: { ...prev[module], [key]: value } }));
  };

  const togglePipeline = (pid) => {
    const current = permissions.negocios.pipelines_permitidos || [];
    const updated = current.includes(pid) ? current.filter(id => id !== pid) : [...current, pid];
    setPerm("negocios", "pipelines_permitidos", updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    const finalPerms = JSON.parse(JSON.stringify(permissions));
    if (allPipelines) finalPerms.negocios.pipelines_permitidos = null;
    else if (!finalPerms.negocios.pipelines_permitidos?.length) finalPerms.negocios.pipelines_permitidos = [];
    try {
      if (editProfile) await axios.put(`${API}/profiles/${editProfile.id}`, { name, permissions: finalPerms, ai_cooldown_minutes: aiCooldown }, { withCredentials: true });
      else await axios.post(`${API}/profiles`, { name, permissions: finalPerms, ai_cooldown_minutes: aiCooldown }, { withCredentials: true });
      setDialogOpen(false); fetchProfiles();
    } catch (err) { setError(err.response?.data?.detail || t("ui.erro_ao_salvar")); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try { await axios.delete(`${API}/profiles/${deleteTarget.id}`, { withCredentials: true }); fetchProfiles(); } catch (err) { setError(err.response?.data?.detail || "Erro ao excluir"); }
    setDeleteTarget(null);
  };

  const filtered = profiles.filter(p => !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const toggle = (section) => setExpanded(prev => ({ ...prev, [section]: !prev[section] }));

  const toggleAllInSection = (mod, actions, allOn) => {
    setPermissions(prev => {
      const updated = { ...prev, [mod]: { ...prev[mod] } };
      actions.forEach(a => { updated[mod][a.key] = !allOn; });
      return updated;
    });
  };

  const PermRow = ({ label, module, actionKey }) => (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-slate-600">{label}</span>
      <Switch checked={!!permissions[module]?.[actionKey]} onCheckedChange={v => setPerm(module, actionKey, v)} />
    </div>
  );

  return (
    <div data-testid="perfis-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <ShieldCheck className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              {t("configuracoes.perfis.title")} <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{filtered.length}</Badge>
            </h1>
            <p className="text-slate-500 mt-0.5">{t("configuracoes.perfis.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar perfil..." className="pl-9 w-full sm:w-48 h-9 text-sm" />
          </div>
          <Tooltip><TooltipTrigger asChild><Button onClick={openNew} size="icon" style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90 h-9 w-9"><Plus className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("configuracoes.perfis.new")}</TooltipContent></Tooltip>
        </div>
      </div>

      <div className="space-y-3">
        {loading ? [...Array(3)].map((_, i) => <div key={i} className="h-20 bg-white rounded-lg border border-slate-200 animate-pulse" />) :
         filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-white rounded-lg border border-slate-200">
            <ShieldCheck className="w-10 h-10 mb-3 text-slate-300" /><p className="text-sm">{t("ui.nenhum_perfil")}</p>
          </div>
        ) : filtered.map(p => (
          <div key={p.id} className="bg-white rounded-lg border border-slate-200 shadow-sm hover:border-slate-400 transition-colors p-4 flex items-center gap-4 cursor-pointer" onClick={() => openEdit(p)}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-lg" style={{ backgroundColor: tc500 }}>
              {p.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-900">{p.name}</span>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{p.user_count || 0} usuário{p.user_count !== 1 ? "s" : ""}</span>
                {Object.entries(MODULE_LABELS).filter(([mod]) => p.permissions?.[mod]?.view).map(([, label]) => (
                  <span key={label} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{label}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Tooltip><TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); openEdit(p); }} className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"><Pencil className="w-4 h-4" /></Button>
              </TooltipTrigger><TooltipContent>{t("common.edit")}</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setDeleteTarget(p); }} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></Button>
              </TooltipTrigger><TooltipContent>{t("common.delete")}</TooltipContent></Tooltip>
            </div>
          </div>
        ))}
      </div>

      {/* Profile Editor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" style={{ color: tc500 }} />
              {editProfile ? "Editar Perfil" : "Novo Perfil"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-slate-700">{t("ui.nome_perfil")}</Label>
              <Input value={name} onChange={e => setName(e.target.value)} required placeholder="Ex: Vendedor, Gerente, Visualizador..." />
            </div>

            {/* Dashboard */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                <span className="text-sm font-semibold text-slate-700">Dashboard</span>
                <Switch checked={!!permissions.dashboard?.view} onCheckedChange={v => setPerm("dashboard", "view", v)} />
              </div>
            </div>

            {/* CRM Modules */}
            {Object.entries(MODULE_LABELS).map(([mod, modLabel]) => {
              const actionsMap = {
                negocios: CRM_ACTIONS, empresas: CRM_ACTIONS, contatos: CRM_ACTIONS, produtos: CRM_ACTIONS,
                tarefas: BASIC_ACTIONS, automacoes: BASIC_ACTIONS, leads: BASIC_ACTIONS,
                email: EMAIL_ACTIONS, relatorios: REPORT_ACTIONS,
                contratos: CONTRATO_ACTIONS, cobrancas: COBRANCA_ACTIONS, whatsapp: WHATSAPP_ACTIONS,
                telefonia: TELEFONIA_ACTIONS, arquivos: ARQUIVO_ACTIONS,
              };
              const actions = actionsMap[mod] || BASIC_ACTIONS;
              const allKeys = mod === "negocios"
                ? [...actions.map(a => a.key), "mover_cards", "ganhar_perder", "somente_proprios", "mover_negocios", "liberar_isento"]
                : actions.map(a => a.key);
              const allOn = allKeys.every(k => permissions[mod]?.[k]);
              return (
              <div key={mod} className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <button type="button" onClick={() => toggle(mod)} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
                    {expanded[mod] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <span className="text-sm font-semibold text-slate-700">{modLabel}</span>
                  </button>
                  <Switch checked={allOn} onCheckedChange={() => {
                    setPermissions(prev => {
                      const updated = { ...prev, [mod]: { ...prev[mod] } };
                      allKeys.forEach(k => { updated[mod][k] = !allOn; });
                      return updated;
                    });
                  }} />
                </div>
                {expanded[mod] && (
                  <div className="px-4 py-2 space-y-0 divide-y divide-slate-100">
                    {actions.map(a => <PermRow key={a.key} label={a.label} module={mod} actionKey={a.key} />)}
                    {mod === "negocios" && (
                      <>
                        <PermRow label="Mover cards entre etapas" module="negocios" actionKey="mover_cards" />
                        <PermRow label="Ganhar / Perder negócios" module="negocios" actionKey="ganhar_perder" />
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-slate-600">{t("ui.apenas_proprios")}</span>
                          <Switch checked={!!permissions.negocios?.somente_proprios} onCheckedChange={v => setPerm("negocios", "somente_proprios", v)} />
                        </div>
                        <div className="flex items-center justify-between py-1.5 border-t border-slate-100 pt-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm text-slate-700">{t("ui.mover_cards")}</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" className="cursor-help"><Info className="w-3.5 h-3.5 text-slate-400" /></button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-xs">{t("ui.responsavel_cadastro_desc")}</TooltipContent>
                            </Tooltip>
                          </div>
                          <Switch checked={!!permissions.negocios?.mover_negocios} onCheckedChange={v => setPerm("negocios", "mover_negocios", v)} />
                        </div>
                        <div className="flex items-center justify-between py-1.5 border-t border-slate-100 pt-2">
                          <span className="text-sm text-slate-700">Liberar negócio com cobrança isenta</span>
                          <Switch checked={!!permissions.negocios?.liberar_isento} onCheckedChange={v => setPerm("negocios", "liberar_isento", v)} />
                        </div>
                        {/* Pipelines permitidos */}
                        <div className="py-2 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">{t("ui.todos_os_pipelines")}</span>
                            <Switch checked={allPipelines} onCheckedChange={v => { setAllPipelines(v); if (v) setPerm("negocios", "pipelines_permitidos", null); else setPerm("negocios", "pipelines_permitidos", []); }} />
                          </div>
                          {!allPipelines && (
                            <div className="mt-2 space-y-1 pl-2">
                              <p className="text-xs text-slate-400 mb-1.5">{t("ui.selecione_pipelines")}</p>
                              {pipelines.length === 0 ? <p className="text-xs text-slate-400">{t("ui.nenhum_pipeline")}</p> :
                                pipelines.map(pipe => {
                                  const checked = (permissions.negocios?.pipelines_permitidos || []).includes(pipe.id);
                                  return (
                                    <button key={pipe.id} type="button" onClick={() => togglePipeline(pipe.id)}
                                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-left text-sm transition-colors ${checked ? "bg-transparent" : "border-slate-200 text-slate-600"}`}
                                      style={checked ? { borderColor: tc500 } : undefined}>
                                      <div className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0"
                                        style={checked ? { borderColor: tc500, backgroundColor: tc500 } : { borderColor: "#cbd5e1" }}>
                                        {checked && <Check className="w-2.5 h-2.5 text-white" />}
                                      </div>
                                      {pipe.name}
                                    </button>
                                  );
                                })
                              }
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )})}

            {/* Configurações */}
            {(() => {
              const configKeys = Object.keys(CONFIG_LABELS).map(k => ({ key: k }));
              const allConfigOn = configKeys.every(a => permissions.config?.[a.key]);
              return (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <button type="button" onClick={() => toggle("config")} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
                    {expanded.config ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <span className="text-sm font-semibold text-slate-700">{t("nav.configuracoes")}</span>
                  </button>
                  <Switch checked={allConfigOn} onCheckedChange={() => toggleAllInSection("config", configKeys, allConfigOn)} />
                </div>
                {expanded.config && (
                  <div className="px-4 py-2 divide-y divide-slate-100">
                    {Object.entries(CONFIG_LABELS).map(([key, label]) => <PermRow key={key} label={label} module="config" actionKey={key} />)}
                  </div>
                )}
              </div>
              );
            })()}

            {error && <p className="text-xs text-rose-500 bg-rose-50 rounded px-3 py-2">{error}</p>}

            {/* AI Cooldown */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
                <Zap className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold text-slate-700">Inteligência Artificial</span>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <Label className="text-sm text-slate-700">Intervalo entre análises IA</Label>
                    <p className="text-[11px] text-slate-400 mt-0.5">Tempo mínimo (em minutos) entre cada análise por negócio. 0 = sem limite.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Input
                      type="number" min={0} max={1440} value={aiCooldown}
                      onChange={e => setAiCooldown(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-20 h-8 text-center text-sm"
                    />
                    <span className="text-xs text-slate-400">min</span>
                  </div>
                </div>
              </div>
            </div>

            <Button type="submit" style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90">
              <Check className="w-4 h-4 mr-2" />{editProfile ? "Salvar Perfil" : "Criar Perfil"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)} title="Excluir perfil" description={`${t("common.confirm_delete")} o perfil "${deleteTarget?.name}"?`} onConfirm={confirmDelete} />
    </div>
  );
}
